import { useEffect, useRef } from "react";

export function Starfield({ stars = 70, dust = 26, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const starList = Array.from({ length: stars }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.3 + 0.3,
      phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.6 + 0.25,
    }));
    const dustList = Array.from({ length: dust }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.8 + 0.6,
      vy: -(Math.random() * 0.0006 + 0.0002),
      sway: Math.random() * 0.0004 + 0.0001,
      phase: Math.random() * Math.PI * 2,
      alpha: Math.random() * 0.45 + 0.15,
    }));

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      for (const s of starList) {
        const tw = 0.25 + 0.75 * Math.abs(Math.sin(t * 0.001 * s.speed + s.phase));
        ctx.beginPath();
        ctx.arc(s.x * w, s.y * h, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(247, 239, 228, ${0.7 * tw})`;
        ctx.fill();
      }
      for (const d of dustList) {
        d.y += d.vy;
        d.x += Math.sin(t * 0.001 + d.phase) * d.sway;
        if (d.y < -0.02) {
          d.y = 1.02;
          d.x = Math.random();
        }
        const pulse = 0.6 + 0.4 * Math.sin(t * 0.0015 + d.phase);
        ctx.beginPath();
        ctx.arc(d.x * w, d.y * h, d.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(214, 175, 98, ${d.alpha * pulse})`;
        ctx.shadowColor = "rgba(214, 175, 98, 0.8)";
        ctx.shadowBlur = 6;
        ctx.fill();
        ctx.shadowBlur = 0;
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [stars, dust]);

  return (
    <canvas
      ref={ref}
      data-testid="starfield-canvas"
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 ${className}`}
    />
  );
}
